<template>
    <v-autocomplete
        v-model="destinationLocationsSelected"
        multiple
        chips
        density="compact"
        hide-details
        :loading="destinationsLoading"
        :disabled="disabled"
        variant="outlined"
        :items="destinationLocations"
        :item-title="itemText"
        :item-value="itemValue"
        clearable
        :label="$t('destination')"
        :return-object="!itemValue"
    >
        <template v-slot:chip="{item, props}">
            <v-chip size="small" v-bind="props" closable @click:close="remove(item.raw)">
                {{ itemText(item.raw) }}
            </v-chip>
        </template>
        <template v-slot:item="{item, props}">
            <v-list-item v-bind="props">
                <template v-slot:title>
                    <v-icon size="small" start>
                        {{
                            destinationLocationsSelected.includes(item.value)
                                ? 'mdi-checkbox-outline'
                                : 'mdi-checkbox-blank-outline'
                        }}
                    </v-icon>
                    <v-icon size="small" start>
                        {{ prependIcon(item.raw) }}
                    </v-icon>
                    <span v-html="itemText(item.raw)" />
                </template>
            </v-list-item>
        </template>
    </v-autocomplete>
</template>

<script lang="ts">
    import {Component, Emit, Prop, VModel, Vue} from 'vue-facing-decorator'

    @Component
    export class DestinationSelectAutocomplete extends Vue {
        @VModel() destinationLocationsSelected
        @Prop({default: false, type: Boolean}) disabled
        @Prop({default: false, type: Boolean}) _destinationsLoading
        @Prop({default: false, type: Boolean}) dense
        @Prop() itemValue

        destinationLocations = []

        async mounted() {
            await this.loadRestrictedLocations()
        }

        @Emit()
        changeDestinationsLoading(val) {
            return val
        }

        @Emit()
        async loadRestrictedLocations() {
            try {
                this.destinationsLoading = true
                const restrictedLocationsRs = await this.$api.restrictedLocations.get()

                if (restrictedLocationsRs.regionFilter === 'ALL') {
                    const countriesRs = await this.$api.locations.get({limitCountries: -1})
                    this.destinationLocations = countriesRs.countries
                        .map(c => ({
                            countryId: c.id,
                            countryName: c.name,
                            id: c.id,
                        }))
                        ?.sort((a, b) => a.countryName?.localeCompare(b.countryName))
                } else if (restrictedLocationsRs.regionFilter === 'SELECTED_ONLY') {
                    const locationsExtended =
                        restrictedLocationsRs.restrictedLocations?.reduce((arr, el) => {
                            arr.push({
                                ...el,
                                id: el.cityId || el.countryId,
                            })

                            if (el.cityName && !arr.find(location => location.id === el.countryId)) {
                                arr.push({
                                    countryId: el.countryId,
                                    countryName: el.countryName,
                                    id: el.countryId,
                                })
                            }

                            return arr
                        }, []) || []

                    this.destinationLocations = locationsExtended
                        ?.sort((a, b) => a.cityName?.localeCompare(b.cityName))
                        ?.sort((a, b) => !!a.cityName - !!b.cityName)
                        ?.sort((a, b) => a.countryName?.localeCompare(b.countryName))
                }
            } catch {
                this.$toast.error(this.$t('error_message.load_error'))
            } finally {
                this.destinationsLoading = false
            }

            return this.destinationLocations
        }

        itemText({countryName, cityName}) {
            return `${countryName || ''}${cityName ? `, ${cityName}` : ''}`
        }

        prependIcon({cityName}) {
            return cityName ? 'mdi-city' : 'mdi-earth'
        }

        remove(item) {
            const index = this.destinationLocationsSelected.indexOf(this.itemValue ? item[this.itemValue] : item)
            if (index >= 0) this.destinationLocationsSelected.splice(index, 1)
        }

        get destinationsLoading() {
            return this._destinationsLoading
        }

        set destinationsLoading(val) {
            this.changeDestinationsLoading(val)
        }
    }

    export default DestinationSelectAutocomplete
</script>
